import { DotsVerticalRounded, LinkAlt } from "@styled-icons/boxicons-regular";
import {
    HappyBeaming,
    InfoSquare,
    MessageRoundedCheck,
    Notification,
    Pencil,
    Share,
    Trash,
} from "@styled-icons/boxicons-solid";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { Message as MessageObject } from "revolt.js";
import styled from "styled-components";
import { decodeTime } from "ulid";
import { openContextMenu } from "preact-context-menu";
import { Text } from "preact-i18n";
import { useEffect, useState } from "preact/hooks";

import { getDisplayName } from "../../../../lib/displayName";
import { internalEmit } from "../../../../lib/eventEmitter";
import { shiftKeyPressed } from "../../../../lib/modifiers";
import { getRenderer } from "../../../../lib/renderer/Singleton";

import { useApplicationState } from "../../../../mobx/State";
import { QueuedMessage } from "../../../../mobx/stores/MessageQueue";

import { emojiDictionary } from "../../../../assets/emojis";
import { modalController } from "../../../../controllers/modals/ModalController";
import Tooltip from "../../../common/Tooltip";
import { RenderEmoji } from "../../../markdown/plugins/emoji";
import Emoji from "../../Emoji";
import { ReactionWrapper } from "../attachments/Reactions";

interface Props {
    reactionsOpen: boolean;
    setReactionsOpen: (v: boolean) => void;
    message: MessageObject;
    queued?: QueuedMessage;
    renderedAsPinned?: boolean;
    className?: string;
}

/** The gap kept between the bar and the message it belongs to. */
const BAR_GAP = -4;

const OverlayBar = styled.div`
    display: flex;
    position: absolute;
    justify-self: end;
    align-self: end;
    align-content: center;
    justify-content: center;
    right: 0;

    bottom: calc(100% + ${BAR_GAP}px);

    // The bar now reaches into the band of the row above, so it has to be
    // painted above that row's content rather than in tree order with it.
    z-index: 1;
    transition: box-shadow 0.1s ease-out;
    border-radius: 5px;
    background: var(--primary-header);
    border: 1px solid var(--background);

    // A resting shadow: the bar floats over the feed now, so it has to read
    // as raised before the pointer reaches it too.
    box-shadow: rgb(0 0 0 / 20%) 0px 2px 10px;

    &:hover {
        box-shadow: rgb(0 0 0 / 25%) 0px 4px 10px;
    }
`;

const Entry = styled.div`
    height: 32px;
    width: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: pointer;
    transition: 0.2s ease background-color;
    color: var(--secondary-foreground);

    img {
        width: 14px;
        height: 14px;
    }

    &:hover {
        background-color: var(--secondary-header);
        color: var(--foreground);
    }

    &:focus {
        border-radius: var(--border-radius);
        box-shadow: 0 0 0 2.5pt var(--accent);
    }

    &:active {
        svg {
            transform: translateY(1px);
        }
    }
`;

const Divider = styled.div`
    margin: 6px 4px;
    width: 0.5px;
    background: var(--tertiary-background);
`;

export const MessageOverlayBar = observer(
    ({
        reactionsOpen,
        setReactionsOpen,
        message,
        queued,
        renderedAsPinned,
        className,
    }: Props) => {
        const client = message.client;
        const isAuthor = message.author_id === client.user!._id;

        const appState = useApplicationState();

        const [copied, setCopied] = useState<"link" | "id">(null!);
        const [extraActions, setExtra] = useState(shiftKeyPressed);

        useEffect(() => {
            const handler = (ev: KeyboardEvent) => setExtra(ev.shiftKey);

            document.addEventListener("keyup", handler);
            document.addEventListener("keydown", handler);

            return () => {
                document.removeEventListener("keyup", handler);
                document.removeEventListener("keydown", handler);
            };
        });

        const inSelectionMode = appState.layout.isInSelection(
            message.channel?._id,
        );
        const messageSelected = inSelectionMode
            ? appState.layout.isSelected(message._id)
            : false;

        return (
            <OverlayBar
                className={className}
                onClick={(e: MouseEvent) => e.stopPropagation()}>
                {appState.emojis.mostRelevantEmojis.map((emoji) => (
                    <Entry
                        key={emoji}
                        onClick={() => {
                            appState.emojis.mostUsed.increaseRank(emoji);
                            message.react(
                                emojiDictionary[
                                    emoji as keyof typeof emojiDictionary
                                ] ?? emoji,
                            );
                        }}>
                        <RenderEmoji match={emoji} />
                    </Entry>
                ))}
                {message.channel?.havePermission("SendMessage") && (
                    <Tooltip content={<Text id="app.message_actions.reply" />}>
                        <Entry
                            onClick={() =>
                                internalEmit("ReplyBar", "add", message)
                            }>
                            <Share size={18} />
                        </Entry>
                    </Tooltip>
                )}
                {message.channel?.havePermission("SendMessage") &&
                    !(inSelectionMode && !messageSelected) && (
                        <Tooltip
                            content={<Text id="app.message_actions.forward" />}>
                            <Entry
                                onClick={() => {
                                    if (inSelectionMode && messageSelected) {
                                        modalController.push({
                                            type: "forward_picker",
                                        });
                                    } else {
                                        modalController.push({
                                            type: "forward_picker",
                                            messageId: message._id,
                                        });
                                    }
                                }}>
                                <Share
                                    size={18}
                                    style={{ transform: "scaleX(-1)" }}
                                />
                            </Entry>
                        </Tooltip>
                    )}

                {message.channel?.havePermission("React") && (
                    <ReactionWrapper
                        open={reactionsOpen}
                        setOpen={setReactionsOpen}
                        message={message}>
                        <Tooltip
                            content={<Text id="app.message_actions.react" />}>
                            <Entry>
                                <HappyBeaming size={18} />
                            </Entry>
                        </Tooltip>
                    </ReactionWrapper>
                )}

                {isAuthor && (
                    <Tooltip content={<Text id="app.message_actions.edit" />}>
                        <Entry
                            onClick={() =>
                                internalEmit(
                                    "MessageRenderer",
                                    "edit_message",
                                    message._id,
                                )
                            }>
                            <Pencil size={18} />
                        </Entry>
                    </Tooltip>
                )}
                {isAuthor ||
                (message.channel &&
                    message.channel.havePermission("ManageMessages")) ? (
                    <Tooltip content={<Text id="app.message_actions.delete" />}>
                        <Entry
                            onClick={(e) =>
                                e.shiftKey
                                    ? message.delete()
                                    : modalController.push({
                                          type: "delete_message",
                                          target: message,
                                      })
                            }>
                            <Trash size={18} color={"var(--error)"} />
                        </Entry>
                    </Tooltip>
                ) : undefined}
                {(() => {
                    const readers = message.readMarks.filter(
                        (r) => r.user_id !== message.author_id,
                    );
                    if (readers.length === 0) return null;

                    return (
                        <Tooltip
                            content={
                                <>
                                    {readers.map((r) => {
                                        const readTime = dayjs(
                                            decodeTime(r._id),
                                        );
                                        const date =
                                            readTime.format("DD.MM.YYYY");
                                        const time = readTime.format("HH:mm");
                                        const isDM =
                                            message.channel?.channel_type ===
                                            "DirectMessage";
                                        const prefix = isDM
                                            ? "Прочитано "
                                            : `${getDisplayName(
                                                  message.client.users.get(
                                                      r.user_id,
                                                  ),
                                              )} `;
                                        return (
                                            <span key={r.user_id}>
                                                {`${prefix}${date} в ${time}`}
                                                <br />
                                            </span>
                                        );
                                    })}
                                </>
                            }
                            onShow={(instance) => {
                                const content = instance.popper.querySelector(
                                    ".tippy-content",
                                ) as HTMLElement | null;
                                if (content) {
                                    content.style.maxWidth = "none";
                                    content.style.whiteSpace = "nowrap";
                                }
                            }}>
                            <Entry>✓✓</Entry>
                        </Tooltip>
                    );
                })()}
                <Tooltip content={<Text id="app.message_actions.more" />}>
                    <Entry
                        onClick={() =>
                            openContextMenu("Menu", {
                                message,
                                contextualChannel: message.channel_id,
                                queued,
                                messageContext: renderedAsPinned
                                    ? "pinned"
                                    : undefined,
                            })
                        }>
                        <DotsVerticalRounded size={18} />
                    </Entry>
                </Tooltip>
                {extraActions && (
                    <>
                        <Divider />
                        <Tooltip content="Mark as Unread">
                            <Entry
                                onClick={() => {
                                    // ! FIXME: deduplicate this code with ctx menu
                                    const messages = getRenderer(
                                        message.channel!,
                                    ).messages;
                                    const index = messages.findIndex(
                                        (x) => x._id === message._id,
                                    );

                                    let unread_id = message._id;
                                    if (index > 0) {
                                        unread_id = messages[index - 1]._id;
                                    }

                                    internalEmit(
                                        "NewMessages",
                                        "mark",
                                        unread_id,
                                    );
                                    message.channel?.ack(unread_id, true);
                                }}>
                                <Notification size={18} />
                            </Entry>
                        </Tooltip>
                        <Tooltip
                            content={
                                copied === "link" ? "Copied!" : "Copy Link"
                            }
                            hideOnClick={false}>
                            <Entry
                                onClick={() => {
                                    setCopied("link");
                                    modalController.writeText(message.url);
                                }}>
                                <LinkAlt size={18} />
                            </Entry>
                        </Tooltip>
                        <Tooltip
                            content={copied === "id" ? "Copied!" : "Copy ID"}
                            hideOnClick={false}>
                            <Entry
                                onClick={() => {
                                    setCopied("id");
                                    modalController.writeText(message._id);
                                }}>
                                <InfoSquare size={18} />
                            </Entry>
                        </Tooltip>
                    </>
                )}
            </OverlayBar>
        );
    },
);
